// src/context/FileBadgeContext.jsx
//
// Per-user preference for which metadata badges (the small pills at the bottom
// of each file card) are shown, configurable per file-type category.
//
// Like ThemeContext, this is a lightweight UI preference persisted to
// localStorage — it does not need to round-trip through the backend.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'qdex-file-badges';

// All badge kinds, in the order they should render on a card.
export const BADGE_DEFS = [
    { key: 'type',       label: 'Format' },
    { key: 'size',       label: 'File size' },
    { key: 'duration',   label: 'Duration' },
    { key: 'dimensions', label: 'Dimensions' },
];

// File-type categories a card can fall into.
export const FILE_CATEGORIES = [
    { key: 'audio', label: 'Audio' },
    { key: 'video', label: 'Video' },
    { key: 'image', label: 'Images' },
    { key: 'other', label: 'Other files' },
];

// Which badges even make sense for a given category. Duration is only
// meaningful for time-based media; dimensions only for things with pixels.
export const APPLICABLE_BADGES = {
    audio: ['type', 'size', 'duration'],
    video: ['type', 'size', 'duration', 'dimensions'],
    image: ['type', 'size', 'dimensions'],
    other: ['type', 'size'],
};

// Default = show every applicable badge (preserves the pre-setting behaviour).
const DEFAULT_PREFS = Object.fromEntries(
    FILE_CATEGORIES.map(({ key }) => [key, [...APPLICABLE_BADGES[key]]])
);

// Map a mimetype to one of the categories above.
export const categoryForMimetype = (mimetype) => {
    if (typeof mimetype === 'string') {
        if (mimetype.startsWith('audio/')) return 'audio';
        if (mimetype.startsWith('video/')) return 'video';
        if (mimetype.startsWith('image/')) return 'image';
    }
    return 'other';
};

// Normalise an arbitrary stored value into a clean prefs object: every category
// present, only applicable badge keys kept, order following APPLICABLE_BADGES.
const sanitise = (raw) => {
    const result = {};
    for (const { key } of FILE_CATEGORIES) {
        const applicable = APPLICABLE_BADGES[key];
        const stored = raw && Array.isArray(raw[key]) ? raw[key] : null;
        result[key] = stored
            ? applicable.filter((b) => stored.includes(b))
            : [...applicable];
    }
    return result;
};

const readStoredPrefs = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return sanitise(DEFAULT_PREFS);
        return sanitise(JSON.parse(stored));
    } catch {
        return sanitise(DEFAULT_PREFS);
    }
};

export const FileBadgeContext = createContext({
    prefs: DEFAULT_PREFS,
    isBadgeEnabled: () => true,
    toggleBadge: () => {},
    resetDefaults: () => {},
});

export const FileBadgeProvider = ({ children }) => {
    const [prefs, setPrefs] = useState(readStoredPrefs);

    const persist = useCallback((next) => {
        setPrefs(next);
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
            /* ignore persistence failures (private mode, quota, etc.) */
        }
    }, []);

    // Keep multiple tabs/windows in sync.
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === STORAGE_KEY) setPrefs(readStoredPrefs());
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const isBadgeEnabled = useCallback(
        (category, badgeKey) => (prefs[category] ?? []).includes(badgeKey),
        [prefs]
    );

    const toggleBadge = useCallback((category, badgeKey) => {
        if (!APPLICABLE_BADGES[category]?.includes(badgeKey)) return;
        const current = prefs[category] ?? [];
        const nextForCategory = current.includes(badgeKey)
            ? current.filter((b) => b !== badgeKey)
            // Re-derive from APPLICABLE_BADGES so the stored order stays canonical.
            : APPLICABLE_BADGES[category].filter((b) => b === badgeKey || current.includes(b));
        persist({ ...prefs, [category]: nextForCategory });
    }, [prefs, persist]);

    const resetDefaults = useCallback(() => {
        persist(sanitise(DEFAULT_PREFS));
    }, [persist]);

    const value = useMemo(
        () => ({ prefs, isBadgeEnabled, toggleBadge, resetDefaults }),
        [prefs, isBadgeEnabled, toggleBadge, resetDefaults]
    );

    return <FileBadgeContext.Provider value={value}>{children}</FileBadgeContext.Provider>;
};

export const useFileBadges = () => useContext(FileBadgeContext);
